import { z } from 'zod';
import { HolidayView } from './absence.js';
import { CalendarDate } from './project.js';
/**
 * Ảnh hưởng của việc lịch đổi lên kế hoạch dự án — APM-1312.
 *
 * ## Vì sao phép tính này tất định, và AI đứng sau nó
 *
 * "Ai nghỉ chồng lên việc nào" là số học thuần: giao khoảng ngày, so với năng
 * lực còn lại, chạy lại CPM. Nó phải chạy **không cần key AI** — luật #6 nói AI
 * là tuỳ chọn. Phần AI làm là thứ số học không làm được: xếp hạng mức nghiêm
 * trọng theo ngữ cảnh, viết câu giải thích, nối với RAID đang mở. Nên view này
 * mang **số và mã task**, không mang câu tiếng Việt nào.
 *
 * ## ⚠️ Vì sao KHÔNG tô mọi thứ thành đỏ
 *
 * Một Gantt mà một phần ba số thanh có màu cảnh báo thì màu đó hết nghĩa trong
 * hai ngày. Nên `tasks` chỉ gồm việc **thật sự** mất người trong khoảng ngày của
 * nó — và ngày nghỉ lễ **không** vào đây: cả đội nghỉ thì không việc nào bất
 * thường hơn việc nào, và tô tất cả là cách nhanh nhất để không ai nhìn màu
 * nữa. Ngày lễ đi ra `holidays` để Gantt vẽ sọc nền.
 */
/**
 * Vì sao một việc bị ảnh hưởng. Một việc chỉ mang **một** lý do — lý do nặng
 * nhất — vì giao diện tô một màu và tooltip nói một câu.
 */
/**
 * ⚠️ Tên là `ScheduleImpactTask`, không phải `ImpactedTask`: cái tên kia đã
 * thuộc về ảnh hưởng của một **change request** (`change-request.ts`). Hai thứ
 * trả lời hai câu khác nhau — "CR này đụng vào việc nào" và "lịch đổi làm việc
 * nào mất người" — và dùng chung một tên là mời hai màn hình đọc sai kiểu nhau.
 */
export const ImpactReason = z.enum([
    /** Người phụ trách đã rời công ty trước khi việc này kết thúc. */
    'assignee_leaving',
    /** Người phụ trách nghỉ phép trong khoảng ngày của việc. */
    'assignee_absent',
    /** Phần cam kết cho dự án này không đủ giờ để làm xong việc trong khoảng đó. */
    'commitment_shortfall',
]);
export const ScheduleImpactTask = z.object({
    taskId: z.uuid(),
    /** `PRJ-12` — để PM bấm sang task, và để AI dẫn chứng bằng mã. */
    taskKey: z.string(),
    title: z.string(),
    startDate: CalendarDate,
    dueDate: CalendarDate,
    assigneeId: z.uuid(),
    /**
     * Tên người phụ trách.
     *
     * Có tên ở đây là **đúng phạm vi**: view này nói về **một** dự án mà người gọi
     * đã vào được, nên tên thành viên trong đó là thứ board đã hiện. Ranh giới
     * "trả số không trả tên" của `APM-504b` chỉ áp cho dữ liệu cộng dồn xuyên dự
     * án — và ở đây không có con số nào của dự án khác.
     */
    assigneeName: z.string(),
    reason: ImpactReason,
    /** Giờ mất đi trong khoảng ngày của việc, so với lịch gốc của người đó. */
    lostHours: z.number().nonnegative(),
    /** Giờ người đó còn thật sự dành được cho việc này trong khoảng đó. */
    availableHours: z.number().nonnegative(),
    /** Ước lượng của việc. `null` = chưa ước lượng, nên không so được. */
    estimateHours: z.number().nonnegative().nullable(),
    /** Nằm trên đường găng: trễ một ngày là cả dự án trễ một ngày. */
    critical: z.boolean(),
    /** Trễ được bao nhiêu ngày mà không đẩy ngày kết thúc dự án. */
    totalFloat: z.number().int(),
    /**
     * Số ngày làm việc cần thêm để hấp thụ phần thiếu.
     *
     * `0` nghĩa là vẫn kịp: mất giờ nhưng phần còn lại vẫn đủ cho ước lượng. Đây
     * là con số dẫn tới độ trượt của cả dự án, không phải `lostHours`.
     *
     * ⚠️ Cũng là `0` khi `unassignable` — xem field đó.
     */
    extraDays: z.number().int().nonnegative(),
    /**
     * Người này **không thể** làm xong việc dù có thêm bao nhiêu ngày.
     *
     * Xảy ra khi họ đã rời công ty: giờ khả dụng bằng 0 mãi mãi, nên "cần thêm N
     * ngày" là một con số bịa — thời gian không mua lại được một người đã đi. Việc
     * này phải **giao lại**, và độ trượt của nó phụ thuộc vào người nhận, thứ hệ
     * thống chưa biết. Nên nó không tham gia phép tính `slipDays`, và giao diện nói
     * "cần giao lại" thay vì một số ngày.
     */
    unassignable: z.boolean(),
    /**
     * `critical` = đẩy ngày kết thúc dự án hoặc không ai làm được; `warning` = còn
     * buffer hấp thụ được.
     *
     * Chỉ hai mức, và chúng phân biệt theo **hệ quả** chứ không theo cảm nhận: một
     * thang năm mức ở đây sẽ là năm màu mà không ai nhớ mức ba khác mức bốn ra sao.
     */
    severity: z.enum(['critical', 'warning']),
});
export const ScheduleImpactView = z.object({
    projectId: z.uuid(),
    scannedAt: z.iso.datetime(),
    tasks: z.array(ScheduleImpactTask),
    /**
     * Số việc bị ảnh hưởng **không** nằm trong `tasks` vì đã chạm trần danh sách.
     *
     * Nói ra chứ không cắt im lặng: một danh sách bị cắt đọc y như "chỉ có nhiêu
     * đó thôi", và PM sẽ lập kế hoạch trên một phần sự thật.
     */
    omittedCount: z.number().int().nonnegative(),
    /** Ngày kết thúc theo kế hoạch hiện tại (việc cuối cùng). */
    plannedEndDate: CalendarDate.nullable(),
    /** Ngày kết thúc sau khi tính phần thiếu vào. Bằng `plannedEndDate` khi không trượt. */
    projectedEndDate: CalendarDate.nullable(),
    /**
     * Độ trượt, tính bằng ngày lịch. `0` = kế hoạch vẫn giữ được.
     *
     * ⚠️ **Không** gồm những việc `unassignable`: độ trượt của một việc phải giao
     * lại phụ thuộc vào người nhận. Cộng một con số đoán vào đây sẽ làm cả con số
     * này mất giá trị — nên chúng nằm trong `tasks` với cờ riêng, và `slipDays` chỉ
     * nói phần **tính được**.
     */
    slipDays: z.number().int().nonnegative(),
    /**
     * Ngày nghỉ lễ trong khung thời gian của Gantt — để vẽ **sọc nền**.
     *
     * Không phải một loại "việc bị ảnh hưởng": ngày lễ đụng vào mọi việc như
     * nhau, nên tô thanh theo nó là tô cả biểu đồ.
     */
    holidays: z.array(HolidayView),
});
/** Trần danh sách việc bị ảnh hưởng. Quá đó thì đọc bảng, không đọc màu. */
export const MAX_IMPACTED_TASKS = 20;
//# sourceMappingURL=schedule-impact.js.map